import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as FileSaver from 'file-saver';
import { UserSettingService } from '../../../../../account/services';
import { User } from '../../../../../auth/models';
import { UserSettings } from '../../../../../auth/models/user/user-settings';
import { ToastService } from '../../../../../main/services/toast';
import { ContractSupplier, Round } from '../../../../models';
import { Contract } from '../../../../models/contract';
import { Tender } from '../../../../models/tender';
import { SealedTenderService, TenderContractsService, TenderContractSuppliersService, TenderService } from '../../../../services';

@Component({
    selector: 'jft-contract-navbar',
    templateUrl: './contract-navbar.component.html',
    styleUrls: ['./contract-navbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContractNavbarComponent implements OnInit {
    public defaultDateFormat: string = 'MMM dd, yyyy';
    public loadingExport: boolean = false;
    public cancellingLocation: boolean = false;
    public tender: Tender;
    public contract: Contract;
    public round: Round;
    public rounds: Round[] = [];
    public suppliers: ContractSupplier[] = [];
    public isCollapsed: boolean = true;
    public isAnalysisPage: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private tenderService: TenderService,
        private contractsService: TenderContractsService,
        private tenderContractSuppliersService: TenderContractSuppliersService,
        private toast: ToastService,
        public user: User,
        private userSettingService: UserSettingService,
        private sealedTenderService: SealedTenderService,
    ) {
        this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };
    }

    public ngOnInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.snapshot.data;

        this.tender = parentData['tender'];
        this.contract = data['contract'];
        this.suppliers = data['suppliers'];
        this.rounds = data['rounds'];
        this.round = this.contract.currentRound;
        this.isAnalysisPage = this.router.url.includes('/analysis/round/');
        this.tenderContractSuppliersService.onAwarded.subscribe(() => this.updateContract());
        this.isCollapsed = this.user.settings.tender_requirements;
    }

    public updateContract() {
        this.contractsService.fetchItem(this.tender.id, this.contract.id).subscribe((contract) => {
            this.contract = contract;
            this.round = this.contract.currentRound;
        }, (e) => {
            this.toast.error(e);
        });
    }

    public exportTenderContract($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        let location = this.contract.location.getLabel();

        this.tenderService.exportTender(this.tender, this.contract).subscribe(result => {
            this.loadingExport = false;
            if (result.type === 'task') {
                this.toast.success(
                    'Export will be ready for download in the notification section',
                    'Requested Export',
                );
                return;
            }
            FileSaver.saveAs(result.blob, `${this.tender.name}${location}-tender-bids.xlsx`);
        }, error => {
            this.loadingExport = false;
        });
    }

    public cancelContract(){
        this.cancellingLocation = true;
        this.contractsService.cancel(this.tender, this.contract).subscribe((contract) => {
            this.cancellingLocation = false;
            this.contract = contract;
            this.toast.success('Location cancelled');
            this.router.navigateByUrl(`/tenders/${this.tender.id}/locations`);
        }, (e) => {
            this.cancellingLocation = false;
            this.toast.error(e);
        });
    }

    public permissionMessage() {
        this.toast.error('You don\'t have permission to access this page');
    }

     public collapseStats() {
        this.isCollapsed = !this.isCollapsed;

        this.userSettingService.update({tender_requirements: this.isCollapsed})
            .subscribe((data: UserSettings) => {
                this.user.settings.tender_requirements = data.tender_requirements;
            }, (e) => {
                this.toast.error(e);
            });
    }

    /**
     * Dashboard / Vertical Analysis / Index Analytics: shown, or locked?
     *
     * One method for all three tabs, because the server answers all three from one predicate:
     * can_access_sealed_tender_for_closed_rounds(company, tender) -- sealed AND any contract's
     * current round still open => 403 (buyer/views/sealed_screen_lock.py). Its browser-side
     * twin is tender.isClosedRounds, which the API computes from exactly the same query
     * (TenderSerializer.get_is_closed_rounds), so the tab and the endpoint cannot drift.
     *
     * The tab is HIDDEN rather than rendered-and-disabled. A tab that renders and then fails
     * is the bug get_effective_custom_fields was written to kill: a child airline under a
     * sealed parent read as realtime in the browser, the tabs rendered, and clicking one
     * returned 403.
     *
     * Sealed is read off BOTH the viewer and the tender's owning airline. The server asks
     * about the viewer; the rest of this template asks about the owner. Where the two ever
     * disagree, the tab hides -- for a lock, over-hiding is recoverable and under-hiding is
     * the leak.
     */
    public canAccessAnalysisScreens(): boolean {
        const sealed = this.sealedTenderService.isSealedTender(this.user.company)
            || !!this.tender?.company?.settings?.isSealedTender();

        if (!sealed) {
            return true;
        }

        return !this.sealedTenderService.hasActiveRounds(this.tender);
    }

    /**
     * Check if dashboard access is allowed for current user and tender type
     */
    public canAccessDashboard(): boolean {
        return this.sealedTenderService.canAccessDashboard(this.user.company, this.round);
    }

    /**
     * Check if analysis access is allowed for current user and tender type
     */
    public canAccessAnalysis(): boolean {
        return this.sealedTenderService.canAccessAnalysis(this.user.company, this.round);
    }

    /**
     * Check if index analysis access is allowed for current user and tender type
     */
    public canAccessIndexAnalysis(): boolean {
        return this.sealedTenderService.canAccessIndexAnalysis(this.user.company, this.round);
    }

    /**
     * Get tooltip message for dashboard restriction
     */
    public getDashboardRestrictionTooltip(): string {
        return this.sealedTenderService.getDashboardRestrictionTooltip();
    }

    /**
     * Get tooltip message for analysis restriction
     */
    public getAnalysisRestrictionTooltip(): string {
        return this.sealedTenderService.getAnalysisRestrictionTooltip();
    }

    /**
     * Get tooltip message for index analysis restriction
     */
    public getIndexAnalysisRestrictionTooltip(): string {
        return this.sealedTenderService.getIndexAnalysisRestrictionTooltip();
    }
}
